/**
 * @zendir/ui — Tailwind CSS v4.2 palette (primitive)
 *
 * Endorsed general-purpose scales for product design use where the Zendir
 * and Astro systems have no opinion (e.g. data-viz categorical colors,
 * semantic success/warning/error). These are the real v4.2 values converted
 * from Tailwind's OKLCH definitions — Figma's copy is still the stale v3
 * hex palette; when the two disagree, this file wins.
 */

export const red = {
  50: "#FEF2F2",
  100: "#FFE2E2",
  200: "#FFCACA",
  300: "#FFA3A4",
  400: "#FF6568",
  500: "#FB2C36",
  600: "#E40016",
  700: "#BF000F",
  800: "#9F0712",
  900: "#82181A",
  950: "#460809",
} as const;

export const orange = {
  50: "#FFF7ED",
  100: "#FFEDD5",
  200: "#FFD7A8",
  300: "#FFB970",
  400: "#FF8B1F",
  500: "#FC7100",
  600: "#EC5600",
  700: "#C43E00",
  800: "#9F2D00",
  900: "#7E2A0C",
  950: "#441306",
} as const;

export const yellow = {
  50: "#FEFCE8",
  100: "#FEF9C2",
  200: "#FFF085",
  300: "#FFE030",
  400: "#F7C900",
  500: "#EAB300",
  600: "#CA8A00",
  700: "#A26200",
  800: "#874C00",
  900: "#733E0A",
  950: "#432004",
} as const;

export const green = {
  50: "#F0FDF4",
  100: "#DCFCE7",
  200: "#B9F8CF",
  300: "#7BF1A8",
  400: "#05DF72",
  500: "#00C65A",
  600: "#00A447",
  700: "#00813A",
  800: "#016630",
  900: "#0D542B",
  950: "#032E15",
} as const;

export const sky = {
  50: "#F0F9FF",
  100: "#DFF2FE",
  200: "#B8E6FE",
  300: "#78D4FF",
  400: "#00BBFD",
  500: "#00A5EA",
  600: "#0084C7",
  700: "#0069A2",
  800: "#005986",
  900: "#024A70",
  950: "#052F4A",
} as const;

export const blue = {
  50: "#EFF6FF",
  100: "#DBEAFE",
  200: "#BEDBFF",
  300: "#91C5FF",
  400: "#56A2FF",
  500: "#3280FF",
  600: "#155DFC",
  700: "#1447E6",
  800: "#193CB8",
  900: "#1C398E",
  950: "#162456",
} as const;

export const indigo = {
  50: "#EEF2FF",
  100: "#E0E7FF",
  200: "#C7D2FF",
  300: "#A4B4FF",
  400: "#7D87FF",
  500: "#6260FF",
  600: "#4F39F6",
  700: "#432DD7",
  800: "#372AAC",
  900: "#312C85",
  950: "#1E1A4D",
} as const;

export const purple = {
  50: "#FAF5FF",
  100: "#F3E8FF",
  200: "#E9D5FF",
  300: "#D8B4FF",
  400: "#BF7EFF",
  500: "#AB4EFF",
  600: "#9810FA",
  700: "#8200D9",
  800: "#6E11B0",
  900: "#59168B",
  950: "#3C0366",
} as const;

export const pink = {
  50: "#FDF2F8",
  100: "#FCE7F3",
  200: "#FCCEE8",
  300: "#FDA5D5",
  400: "#FB64B6",
  500: "#F6339A",
  600: "#E30076",
  700: "#C2005C",
  800: "#A2004C",
  900: "#861043",
  950: "#510424",
} as const;

export const rose = {
  50: "#FFF1F2",
  100: "#FFE4E6",
  200: "#FFCCD3",
  300: "#FFA3AE",
  400: "#FF6880",
  500: "#FF2357",
  600: "#E60045",
  700: "#C1003A",
  800: "#A30037",
  900: "#8B0836",
  950: "#4D0218",
} as const;

export const slate = {
  50: "#F8FAFC",
  100: "#F1F5F9",
  200: "#E2E8F0",
  300: "#CAD5E2",
  400: "#90A1B9",
  500: "#62748E",
  600: "#45556C",
  700: "#314158",
  800: "#1D293D",
  900: "#0F172B",
  950: "#020618",
} as const;

export const neutral = {
  50: "#FAFAFA",
  100: "#F5F5F5",
  200: "#E5E5E5",
  300: "#D4D4D4",
  400: "#A1A1A1",
  500: "#737373",
  600: "#525252",
  700: "#404040",
  800: "#262626",
  900: "#171717",
  950: "#0A0A0A",
} as const;

/** All Tailwind scales keyed by display name (drives Foundation/Palette). */
export const tailwindScales = {
  Red: red,
  Orange: orange,
  Yellow: yellow,
  Green: green,
  Sky: sky,
  Blue: blue,
  Indigo: indigo,
  Purple: purple,
  Pink: pink,
  Rose: rose,
  Slate: slate,
  Neutral: neutral,
} as const;
